/* Component is used in both standard env and next.js env. If adding data-fetching refactor SearchBarContainer and pass down data as props. */

/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import type { ChangeEvent } from 'react';
import * as React from 'react';

import classNames from 'classnames';
import URI from 'jsuri';
import PropTypes from 'prop-types';
import type { LegacyContextType } from 'types/legacy-context-types';

import Retracked from 'js/app/retracked';

import type { DiscoveryCollection } from 'bundles/browse-common/legacy/types/DiscoveryCollections';
import MagnifierSvg from 'bundles/page-header/components/icons/MagnifierSvg';
import type { Props as AutoCompleteProps } from 'bundles/search-common/components/searchbar/autocomplete/AutocompleteWrapper';
import HeroBannerSearchBar from 'bundles/search-common/components/searchbar/hero/HeroBannerSearchBar';
import SearchBarLazyLoadInput from 'bundles/search-common/components/searchbar/hero/SearchBarLazyLoadingInput';
import { ON_SITE_SEARCH_PATH, SEARCH_BAR_LOCATION, SEARCH_MAIN_INDEX_ID } from 'bundles/search-common/constants';
import type { SearchBarLocation } from 'bundles/search-common/constants';
import { SearchContext } from 'bundles/search-common/providers/SearchContext';
import { saveRecentlySearched } from 'bundles/search-common/utils/SearchUtils';
import { shouldAllowSearchNavigation } from 'bundles/search-common/utils/emptyQueryValidation';

import _t from 'i18n!nls/search-common';

import 'css!./__styles__/SearchBar';

type Props = {
  hideSearch?: boolean;
  searchIsOpen?: boolean;
  isSearchPage?: boolean;
  pageLocation?: SearchBarLocation;
  shouldFocusSearch?: boolean;
  onBlur?: () => void;
  onFocus?: () => void;
  hideMobileSearchPage?: () => void;
  searchInputIsFocused?: boolean;
  collectionRecommendations?: DiscoveryCollection | null;
  enableOneStepSearch?: boolean;
  shouldShowExposedSearchAndReg?: boolean;
  smallSearchBar?: boolean;
  className?: string;
};

type State = {
  searchText: string;
  autoCompleteLoaded: boolean;
};

const styles = {
  searchBarInGrid: css`
    width: 100%;
    margin-left: 0;
  `,
  criticalSearchBarLayout: css`
    display: flex;
    align-items: center;
  `,
  criticalButtonLayout: css`
    display: contents;
  `,
  preUnifiedSearchBar: css`
    /* As per PageHeader.styl, mobile header kicks in at 1060px; these styles are for desktop only */
    @media (min-width: 1061px) {
      #algolia-placeholder-search-input {
        border-radius: 24px !important;
        padding: var(--cds-spacing-100) var(--cds-spacing-200) !important;
        border: 1px solid var(--cds-color-grey-50) !important;
        color: var(--cds-color-grey-700) !important;
        line-height: 24px !important;
        font-size: 14px !important;
        /* stylelint-disable-next-line font-family-no-missing-generic-family-keyword */
        font-family: 'Open Sans' !important;
        height: 44px !important;
      }

      .search-button .magnifier-wrapper {
        right: 3px;
        border-radius: 24px;
        width: 32px;
        height: 32px;
        padding: var(--cds-spacing-100);
        margin-right: var(--cds-spacing-50);
        margin-top: 6px;
        border: 1px solid transparent;
      }
    }
  `,
};

class SearchBarContent extends React.Component<Props, State> {
  declare LoadableAutoComplete: React.ComponentType<AutoCompleteProps> | null;

  static contextTypes = {
    router: PropTypes.object.isRequired,
    _eventData: PropTypes.object,
  };

  declare context: LegacyContextType<typeof SearchBarContent.contextTypes>;

  static defaultProps = {
    hideSearch: false,
    isSearchPage: false,
    enableOneStepSearch: false,
    shouldShowExposedSearchAndReg: false,
  };

  constructor(props: Props, context: LegacyContextType<typeof SearchBarContent.contextTypes>) {
    super(props);
    const query = context.router?.location?.query?.query;
    this.state = {
      searchText: (Array.isArray(query) ? query[0] : query) || '',
      autoCompleteLoaded: false,
    };

    this.LoadableAutoComplete = null;
  }

  getInitialSearchText = (queryParam: string | string[]): string => {
    return (Array.isArray(queryParam) ? queryParam[0] : queryParam) || '';
  };

  componentDidMount() {
    this.loadAutoComplete();
  }

  loadAutoComplete = () => {
    import('bundles/search-common/components/searchbar/autocomplete/AutocompleteWrapper').then((Mod) => {
      this.LoadableAutoComplete = Mod.default;
      this.setState(() => ({
        autoCompleteLoaded: true,
      }));
    });
  };

  onChange = (event: ChangeEvent<HTMLInputElement>) => {
    this.setState({ searchText: event.target.value });
  };

  onSubmit = (e: React.SyntheticEvent) => {
    e.preventDefault();
    const {
      router,
      router: { location },
      _eventData,
    } = this.context;
    const { isSearchPage, hideMobileSearchPage } = this.props;
    const { searchText } = this.state;

    // Check if navigation should be allowed for this query
    if (!shouldAllowSearchNavigation(searchText, location.query)) {
      // Prevent navigation for organic empty searches
      return;
    }

    const encodedSearchText = encodeURIComponent(searchText.replace(/\s+/g, ' '));

    // Only track and hide mobile search page when search actually proceeds
    Retracked.trackComponent(
      _eventData,
      { searchText, page: window.location.pathname },
      'algolia_search_page_query_updated',
      'search'
    );

    // Save to recent searches
    saveRecentlySearched(searchText);

    // Hide mobile search page since search is proceeding
    hideMobileSearchPage?.();

    const otherQueryParamsString = new URI(location.search).deleteQueryParam('query').query();
    const urlWithQuery = new URI(ON_SITE_SEARCH_PATH)
      .setQuery(otherQueryParamsString)
      .deleteQueryParam('page')
      .deleteQueryParam('sortBy')
      .addQueryParam('query', encodedSearchText, 0)
      .toString();
    if (isSearchPage) {
      const newSearchProviderUrl = new URI(ON_SITE_SEARCH_PATH).addQueryParam('query', encodedSearchText, 0).toString();
      router.push(newSearchProviderUrl);
    } else {
      window.location.assign(urlWithQuery);
    }
  };

  renderSearchBar = () => {
    const {
      // using false here because the default prop is false
      isSearchPage = false,
      pageLocation,
      shouldFocusSearch,
      onBlur,
      onFocus,
      searchIsOpen,
      hideMobileSearchPage,
      enableOneStepSearch,
      shouldShowExposedSearchAndReg,
      searchInputIsFocused,
      collectionRecommendations,
    } = this.props;
    const { LoadableAutoComplete } = this;
    const { searchText, autoCompleteLoaded } = this.state;
    const autoCompleteShouldFocus = shouldFocusSearch || searchInputIsFocused;
    if ((enableOneStepSearch || shouldShowExposedSearchAndReg) && autoCompleteLoaded && LoadableAutoComplete) {
      return (
        <LoadableAutoComplete
          onSearchFocus={onFocus}
          searchIsOpen={searchIsOpen}
          hideMobileSearchPage={hideMobileSearchPage}
          shouldShowExposedSearchAndReg={shouldShowExposedSearchAndReg}
          enableOneStepSearch={enableOneStepSearch}
          shouldFocusSearch={autoCompleteShouldFocus}
          collectionRecommendations={collectionRecommendations}
          isSearchPage={isSearchPage}
          startingQuery={searchText}
        />
      );
    }

    if (autoCompleteLoaded && LoadableAutoComplete) {
      return (
        <LoadableAutoComplete
          collectionRecommendations={collectionRecommendations}
          isSearchPage={isSearchPage}
          pageLocation={pageLocation}
          onSearchFocus={onFocus}
          searchIsOpen={searchIsOpen}
          hideMobileSearchPage={hideMobileSearchPage}
          shouldFocusSearch={autoCompleteShouldFocus}
          startingQuery={searchText}
        />
      );
    }

    return pageLocation === SEARCH_BAR_LOCATION.FrontPage || pageLocation === SEARCH_BAR_LOCATION.FrontPageBanner ? (
      <HeroBannerSearchBar
        isFocused={autoCompleteShouldFocus ?? false}
        isLazyLoading={true}
        pageLocation={pageLocation}
      >
        <SearchBarLazyLoadInput value={searchText} onBlur={onBlur} onFocus={onFocus} onChange={this.onChange} />
      </HeroBannerSearchBar>
    ) : (
      <SearchContext.Consumer>
        {(context) => {
          const searchData = context?.getSearchResults?.(SEARCH_MAIN_INDEX_ID);
          const { aiSearchMetadata } = searchData || {};
          const isAISummaryEnabled = aiSearchMetadata?.isEligibleForAiGeneratedSummary && isSearchPage;
          return (
            <div
              className="rc-SearchBar__container horizontal-box"
              css={[styles.criticalSearchBarLayout, styles.preUnifiedSearchBar]}
            >
              <div className="mobile-magnifier">
                <MagnifierSvg fill="var(--cds-color-blue-500)" />
              </div>
              <input
                id="algolia-placeholder-search-input"
                type="text"
                // This is necessary to allow the search bar to work without rehydration
                name="query"
                placeholder={isAISummaryEnabled ? _t('Ask Coursera AI or Search') : _t('What do you want to learn?')}
                value={searchText}
                onBlur={onBlur}
                onFocus={onFocus}
                onChange={this.onChange}
                autoComplete="off"
                aria-label={_t('Search catalog')}
              />
              <button
                type="submit"
                className="nostyle search-button"
                css={styles.criticalButtonLayout}
                aria-label={_t('Submit Search')}
              >
                <MagnifierSvg fill="var(--cds-color-grey-25)" />
              </button>
            </div>
          );
        }}
      </SearchContext.Consumer>
    );
  };

  render() {
    const { hideSearch, shouldShowExposedSearchAndReg, enableOneStepSearch, smallSearchBar } = this.props;
    const { autoCompleteLoaded } = this.state;

    if (hideSearch) {
      return null;
    }

    // After lazy loading, the AutoComplete will handle the operations, but before
    // we still want to allow the user to search & be redirected accordingly
    const formProps = autoCompleteLoaded
      ? { onSubmit: (e: React.SyntheticEvent) => e.preventDefault() }
      : { onSubmit: this.onSubmit };
    const searchBarRootClasses = classNames('rc-SearchBar horizontal-box isLohpRebrand', {
      shouldShowExposedSearchAndReg,
      enableOneStepSearch: enableOneStepSearch && !shouldShowExposedSearchAndReg,
      'small-search-bar': smallSearchBar,
    });

    return (
      <div className={searchBarRootClasses} css={styles.searchBarInGrid}>
        <form className="search-form" role="search" {...formProps}>
          {this.renderSearchBar()}
        </form>
      </div>
    );
  }
}

export default SearchBarContent;
